import React from "react";
import {
  Box,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip as MuiTooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import HomeIcon from "@mui/icons-material/Home";

export const RestaurantProductPerformance = ({ startDate, endDate, data }) => {
  const theme = useTheme();

  // Find max values for progress bars
  const maxRevenue = data?.length
    ? Math.max(...data.map((item) => item.totalRevenue))
    : 0;

  // Prepare data for bar chart - sort by total revenue
  const chartData = [...(data || [])].sort(
    (a, b) => a.totalRevenue - b.totalRevenue
  );

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <HomeIcon
          size={24}
          color={theme.palette.primary.main}
          style={{ marginRight: 8 }}
        />
        <Typography variant="h6" fontWeight="600">
          Desempenho por Restaurante
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {startDate ? new Date(startDate).toLocaleDateString() : ""} -{" "}
        {endDate ? new Date(endDate).toLocaleDateString() : ""}
      </Typography>

      <Box sx={{ height: 200, mb: 3 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            layout="vertical"
            data={chartData}
            margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" tickFormatter={(value) => `R$ ${value}`} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} />
            <Tooltip
              formatter={(value) => [`R$ ${value.toFixed(2)}`, "Receita Total"]}
              labelFormatter={(label) => `Restaurante: ${label}`}
            />
            <Legend />
            <Bar
              dataKey="totalRevenue"
              name="Receita Total"
              fill={theme.palette.primary.main}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Restaurante</TableCell>
              <TableCell align="right">Produtos</TableCell>
              <TableCell align="right">Produto Mais Vendido</TableCell>
              <TableCell align="right">Ticket Médio</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map((restaurant) => (
              <TableRow key={restaurant.name}>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <HomeIcon size={16} style={{ marginRight: 8 }} />
                    <Typography variant="body2" fontWeight={500}>
                      {restaurant.name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">{restaurant.productCount}</TableCell>
                <TableCell align="right">
                  <MuiTooltip title={restaurant.topSellingProduct} arrow>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 150,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        cursor: "default",
                      }}
                    >
                      {restaurant.topSellingProduct}
                    </Typography>
                  </MuiTooltip>
                </TableCell>
                <TableCell align="right">
                  R$ {restaurant.avgOrderValue.toFixed(2)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Progresso de Receita Total
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
          {data?.slice(0, 3).map((restaurant) => (
            <Box key={restaurant.name}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography variant="body2">{restaurant.name}</Typography>
                <Typography variant="body2">
                  R$ {restaurant.totalRevenue.toFixed(2)}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(restaurant.totalRevenue / maxRevenue) * 100}
                sx={{
                  height: 6,
                  borderRadius: 1,
                  backgroundColor: theme.palette.grey[200],
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: theme.palette.primary.main,
                  },
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};
